import { PropsWithChildren, ReactNode, useState } from 'react'
import { EllipsisVertical, Loader2 } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import BlizzardIcon from 'app/frontend/base/components/BlizzardIcon'
import CharacterClassIcon from 'app/frontend/base/components/CharacterClassIcon'
import CharacterRenameModal, {
  CharacterRenameValues,
} from 'app/frontend/base/components/CharacterRenameModal'
import {
  getRolesWithMultipleSpecs,
  getSpecializationsByClass,
} from 'app/frontend/base/services/characterValues'
import CharacterClass, {
  classColours,
} from 'app/frontend/types/custom/CharacterClass'
import CharacterRole from 'app/frontend/types/custom/CharacterRole'
import {
  ClassMenuItems,
  MainSpecMenuItems,
} from 'app/frontend/base/pages/TeamDashboard/subpages/TeamRoster/components/TeamMemberEditMenus'

type SpecOptions = {
  role: CharacterRole
  specId: number | null
  specDetails: { name: string; icon: string } | null
  onChange: (specId: number) => void
  onRoleChange?: (role: CharacterRole) => void
}

type Props = PropsWithChildren<{
  character: wowaudit.CharacterReference
  description?: ReactNode
  size?: 'small' | 'medium'
  busy?: boolean
  spec?: SpecOptions
  onClassChange: (characterClass: CharacterClass) => void
  onRename: (values: CharacterRenameValues, onSuccess: () => void) => void
  renaming?: boolean
  removeLabel: string
  onRemove: () => void
}>

const ManualCharacterContextMenu = ({
  character,
  description,
  size = 'small',
  busy,
  spec,
  onClassChange,
  onRename,
  renaming,
  removeLabel,
  onRemove,
  children,
}: Props) => {
  const [open, setOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)

  return (
    <>
      {renameOpen && (
        <CharacterRenameModal
          character={character}
          isPending={renaming}
          onSubmit={(values) => onRename(values, () => setRenameOpen(false))}
          onClose={() => setRenameOpen(false)}
        />
      )}

      <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
        <DropdownMenuTrigger disabled={busy}>
          <div className='flex items-center justify-center cursor-pointer p-2 md:p-1'>
            {busy ? (
              <Loader2
                className={`${
                  size === 'small' ? 'w-3.5 h-3.5' : 'w-5 h-5'
                } animate-spin`}
              />
            ) : (
              <EllipsisVertical size={size === 'small' ? 14 : 18} />
            )}
          </div>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align='end'
          alignOffset={-10}
          onCloseAutoFocus={(event) => event.preventDefault()}
        >
          <DropdownMenuLabel>
            <div className='flex items-center gap-1.5'>
              <CharacterClassIcon
                characterClass={character.characterClass}
                size='small'
              />
              <span className={classColours[character.characterClass].text}>
                {character.name}
              </span>
            </div>
            {description && (
              <div className='opacity-70 italic font-normal mt-2'>
                {description}
              </div>
            )}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          {spec && (
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <div className='flex flex-col items-start mt-[-0.1rem]'>
                  <span className='grow'>Set spec</span>
                  {spec.specDetails ? (
                    <span className='text-xs text-muted-foreground flex items-center gap-1.5'>
                      <BlizzardIcon
                        iconName={spec.specDetails.icon}
                        size='small'
                      />
                      {spec.specDetails.name}
                    </span>
                  ) : null}
                </div>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent>
                  <MainSpecMenuItems
                    member={{ role: spec.role, specId: spec.specId }}
                    classSpecializations={getSpecializationsByClass(
                      character.classId,
                    )}
                    rolesWithMultipleSpecs={
                      spec.onRoleChange
                        ? getRolesWithMultipleSpecs(character.classId)
                        : []
                    }
                    onRoleChange={(role) =>
                      (role !== spec.role || spec.specId !== null) &&
                      spec.onRoleChange?.(role)
                    }
                    onSpecChange={(specId) =>
                      specId !== spec.specId && spec.onChange(specId)
                    }
                  />
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          )}

          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <div className='flex flex-col items-start mt-[-0.1rem]'>
                <span className='grow'>Change class</span>
                <span className='text-xs text-muted-foreground'>
                  {character.characterClass}
                </span>
              </div>
            </DropdownMenuSubTrigger>
            <DropdownMenuPortal>
              <DropdownMenuSubContent>
                <ClassMenuItems
                  currentClass={character.characterClass}
                  onClassChange={(characterClass) =>
                    characterClass !== character.characterClass &&
                    onClassChange(characterClass)
                  }
                />
              </DropdownMenuSubContent>
            </DropdownMenuPortal>
          </DropdownMenuSub>

          <DropdownMenuItem onSelect={() => setRenameOpen(true)}>
            Change name
          </DropdownMenuItem>
          {children}
          <DropdownMenuItem onSelect={onRemove}>
            <span className='text-destructive-foreground'>{removeLabel}</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

export default ManualCharacterContextMenu
